import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings,
  Users,
  Edit,
  Save,
  X,
  Loader,
  Calendar,
  Clock,
  Calculator,
  AlertCircle,
  DollarSign,
  Info,
  CheckCircle
} from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';

const PayrollSetup = ({
  staff,
  payrollSettings,
  loading,
  actionLoading,
  updatePayrollSettings,
  updateStaffPayroll,
  fetchStaffData,
  setError,
  setSuccess
}) => {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const getAuthToken = () => {
    return localStorage.getItem('adminAuthToken') || localStorage.getItem('kioskAuthToken') || localStorage.getItem('authToken');
  };

  // FIXED: Filter for active staff only
  const activeStaff = useMemo(() => staff?.filter(s => s.status === 'active') || [], [staff]);

  // State management
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSettingsEdit, setShowSettingsEdit] = useState(false);
  const [staffSaveSuccess, setStaffSaveSuccess] = useState(false);
  const [settingsSaveSuccess, setSettingsSaveSuccess] = useState(false);
  const [selectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [showBackfillModal, setShowBackfillModal] = useState(false);
 
  // ADDED: Force refresh trigger for calculated rates
  const [refreshKey, setRefreshKey] = useState(0);

  // Settings form state for shift-based system
  const [deductionSettings, setDeductionSettings] = useState({
    useShiftBasedRates: payrollSettings?.useShiftBasedRates !== false,
    latenessGraceMinutes: payrollSettings?.latenessGraceMinutes || 5,
    earlyLeaveGraceMinutes: payrollSettings?.earlyLeaveGraceMinutes || 5,
    absencePenalty: payrollSettings?.absencePenalty || 'shift_based',
    overtimeRateMultiplier: payrollSettings?.overtimeRateMultiplier || 1.5
  });

  // Staff payroll config state
  const [staffPayrollConfig, setStaffPayrollConfig] = useState({
    baseSalary: '',
    housingAllowance: 0,
    transportAllowance: 0,
    mealAllowance: 0,
    otherAllowances: 0,
    taxPercentage: 0,
    pensionPercentage: 0,
    bankDetails: { bankName: '', accountNumber: '', accountName: '' }
  });

  // UPDATED: Dynamic working days calculation - TOTAL DAYS IN MONTH
  const calculateWorkingDaysForMonth = (year, month) => {
    // Simply return the number of days in the month (staff-friendly rates)
    const lastDay = new Date(year, month, 0); // Day 0 of next month = last day of current month
    return lastDay.getDate(); // Returns 31 for Oct, 28/29 for Feb, 30 for Sep, etc.
  };

  // Get dynamic working days for current month
  const dynamicWorkingDays = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    return calculateWorkingDaysForMonth(year, month);
  }, [selectedMonth]);

  // Format currency
  const formatCurrency = (amount) => `₦${Math.round(amount || 0).toLocaleString('en-NG')}`;

  // Save deduction settings
  const saveDeductionSettings = useCallback(async () => {
    try {
      await updatePayrollSettings(deductionSettings);
      setSettingsSaveSuccess(true);
      setTimeout(() => {
        setShowSettingsEdit(false);
        setSettingsSaveSuccess(false);
      }, 1500);
    } catch (err) {
      // Error handled in parent
    }
  }, [deductionSettings, updatePayrollSettings]);

  // Handle staff selection for editing
  const handleStaffSelect = useCallback((staff) => {
    setSelectedStaff(staff);
    setStaffPayrollConfig({
      baseSalary: staff.baseSalary || '',
      housingAllowance: staff.housingAllowance || 0,
      transportAllowance: staff.transportAllowance || 0,
      mealAllowance: staff.mealAllowance || 0,
      otherAllowances: staff.otherAllowances || 0,
      taxPercentage: staff.taxPercentage || 0,
      pensionPercentage: staff.pensionPercentage || 0,
      bankDetails: staff.bankDetails || { bankName: '', accountNumber: '', accountName: '' }
    });
    setShowEditModal(true);
  }, []);

  // UPDATED: Save staff payroll configuration with FULL REFRESH
  const saveStaffPayrollConfig = useCallback(async () => {
    try {
      await updateStaffPayroll(selectedStaff._id, staffPayrollConfig);
      
      // CRITICAL: Fetch fresh staff data from server to ensure real-time updates
      const updatedStaffData = await fetchStaffData();
      
      // Find the updated staff member from fresh data
      const updatedStaff = updatedStaffData.find(s => s._id === selectedStaff._id);
      
      if (updatedStaff) {
        // Update local state with fresh data
        setSelectedStaff(updatedStaff);
        setStaffPayrollConfig({
          baseSalary: updatedStaff.baseSalary || '',
          housingAllowance: updatedStaff.housingAllowance || 0,
          transportAllowance: updatedStaff.transportAllowance || 0,
          mealAllowance: updatedStaff.mealAllowance || 0,
          otherAllowances: updatedStaff.otherAllowances || 0,
          taxPercentage: updatedStaff.taxPercentage || 0,
          pensionPercentage: updatedStaff.pensionPercentage || 0,
          bankDetails: updatedStaff.bankDetails || { bankName: '', accountNumber: '', accountName: '' }
        });
        setRefreshKey(prev => prev + 1);
      }

      setStaffSaveSuccess(true);
      setTimeout(() => {
        setStaffSaveSuccess(false);
        setShowEditModal(false);
        setSelectedStaff(null);
      }, 1500);

    } catch (err) {
      // Error handled in parent
    }
  }, [selectedStaff, staffPayrollConfig, updateStaffPayroll, fetchStaffData]);

  // Calculated Rates Display Component with refresh support
  const CalculatedRatesDisplay = ({ baseSalary, staffId, refreshKey }) => {
    const [rates, setRates] = useState(null);
    const [ratesLoading, setRatesLoading] = useState(true);
    
    useEffect(() => {
      async function calculateRates() {
        try {
          if (!baseSalary || baseSalary <= 0 || !staffId) {
            setRatesLoading(false);
            return;
          }

          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/api/attendance/shifts?staffId=${staffId}`,
            { headers: { Authorization: `Bearer ${getAuthToken()}` } }
          );
          
          if (!response.ok) throw new Error('Failed to fetch shifts');

          const data = await response.json();
          const fullDayShift = data.shifts?.find(s => s.isFullDayReference);
          
          if (!fullDayShift) {
            setRates({ error: 'No full-day reference shift set' });
            setRatesLoading(false);
            return;
          }
          
          const [startH, startM] = fullDayShift.resumptionTime.split(':').map(Number);
          const [endH, endM] = fullDayShift.closingTime.split(':').map(Number);
          const shiftHours = ((endH * 60 + endM) - (startH * 60 + startM)) / 60;
          
          const workingDays = dynamicWorkingDays;
          const fullDayRate = Math.round(baseSalary / workingDays);
          const perMinuteRate = (fullDayRate / (shiftHours * 60)).toFixed(2);
          
          setRates({
            workingDays,
            fullDayRate,
            perMinuteRate,
            shiftHours: shiftHours.toFixed(1),
            shiftTime: `${fullDayShift.resumptionTime} - ${fullDayShift.closingTime}`,
            dayOfWeek: fullDayShift.dayOfWeek
          });
          setRatesLoading(false);
        } catch (err) {
          console.error('Rate calculation error:', err);
          setRates({ error: 'Failed to calculate rates' });
          setRatesLoading(false);
        }
      }
      
      calculateRates();
    }, [baseSalary, staffId, refreshKey]); // ADDED refreshKey as dependency
    
    if (ratesLoading) {
      return (
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <Loader className="animate-spin" size={16} />
          Calculating rates...
        </div>
      );
    }
    
    if (rates?.error) {
      return (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
          {rates.error === 'No full-day reference shift set' ? (
            <span className="flex items-center gap-2">
              <AlertCircle size={16} />
              Please set a full-day reference shift in Shift Management
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <AlertCircle size={16} />
              {rates.error}
            </span>
          )}
        </div>
      );
    }
    
    if (!rates) return null;
    
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <p className="text-gray-600 mb-1 text-xs">Working Days</p>
          <p className="font-bold text-gray-900 text-lg">{rates.workingDays} days</p>
        </div>
        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
          <p className="text-green-700 mb-1 text-xs font-medium">Full Day Rate</p>
          <p className="font-bold text-green-600 text-lg">{formatCurrency(rates.fullDayRate)}</p>
        </div>
        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
          <p className="text-blue-700 mb-1 text-xs font-medium">Full Shift Hours</p>
          <p className="font-bold text-blue-600 text-lg">{rates.shiftHours}hrs</p>
          <p className="text-xs text-blue-500 mt-1 truncate">{rates.shiftTime}</p>
          <p className="text-xs text-blue-400 truncate">({rates.dayOfWeek})</p>
        </div>
        <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
          <p className="text-purple-700 mb-1 text-xs font-medium">Per-Minute Rate</p>
          <p className="font-bold text-purple-600 text-lg">₦{rates.perMinuteRate}/min</p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Deduction Settings Card */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Settings className="text-blue-600" size={24} />
            Payroll & Deduction Settings
          </h2>
          <button
            onClick={() => setShowSettingsEdit(!showSettingsEdit)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Edit size={18} />
            {showSettingsEdit ? 'Cancel' : 'Edit Settings'}
          </button>
        </div>

        {/* Enhanced Working Schedule Display */}
<div className="mb-8 p-6 bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 rounded-xl border-2 border-green-200 shadow-sm">
  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
    <div className="flex-1">
      <h3 className="text-2xl font-bold text-gray-900 mb-3 flex items-center gap-3">
        <Calendar className="text-green-600" size={28} />
        Working Schedule for {new Date(selectedMonth + '-01').toLocaleDateString('en-US', { 
          month: 'long', 
          year: 'numeric' 
        })}
      </h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg p-4 border border-green-200 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Calendar className="text-green-600" size={20} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-600 truncate">Working Days (Payroll)</p>
              <p className="text-3xl font-bold text-green-600">{dynamicWorkingDays}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">All calendar days (including Sundays)</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-purple-200 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Calculator className="text-purple-600" size={20} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-600 truncate">Rate System</p>
              <p className="text-2xl font-bold text-purple-600">Shift-Based</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">Dynamic per-minute rates</p>
        </div>
      </div>
    </div>
  </div>
</div>

        {/* Shift-Based System Display */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Info className="text-purple-600" size={20} />
            Deduction System: Shift-Based Dynamic Rates
          </h3>
          <p className="text-sm text-gray-700 mb-4">
            Deductions are calculated based on each staff member's actual salary and shift hours.
            Rates are personalized and automatically calculated per staff member.
          </p>
          
          {showSettingsEdit ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lateness Grace Period (minutes)
                  </label>
                  <input
                    type="number"
                    value={deductionSettings.latenessGraceMinutes}
                    onChange={(e) => setDeductionSettings({
                      ...deductionSettings,
                      latenessGraceMinutes: parseInt(e.target.value) || 5
                    })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    min="0"
                    max="15"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    No deduction if late ≤ this many minutes
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Early Leave Grace Period (minutes)
                  </label>
                  <input
                    type="number"
                    value={deductionSettings.earlyLeaveGraceMinutes}
                    onChange={(e) => setDeductionSettings({
                      ...deductionSettings,
                      earlyLeaveGraceMinutes: parseInt(e.target.value) || 5
                    })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    min="0"
                    max="15"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    No deduction if early ≤ this many minutes
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Absence Penalty Type
                  </label>
                  <select
                    value={deductionSettings.absencePenalty}
                    onChange={(e) => setDeductionSettings({
                      ...deductionSettings,
                      absencePenalty: e.target.value
                    })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="shift_based">Shift-Based (Full/Half Day)</option>
                    <option value="full_day">Always Full Day</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Overtime Rate Multiplier
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={deductionSettings.overtimeRateMultiplier}
                    onChange={(e) => setDeductionSettings({
                      ...deductionSettings,
                      overtimeRateMultiplier: parseFloat(e.target.value) || 1.5
                    })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    min="1"
                  />
                </div>
              </div>

              <div className="mt-4 p-4 bg-white rounded-lg border">
                <h4 className="font-semibold text-gray-800 mb-2">Example Calculation:</h4>
                <p className="text-sm text-gray-700 mb-2">
                  For a staff earning ₦100,000/month with {dynamicWorkingDays} working days
                  and 10-hour shifts:
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Full day rate: ₦{Math.round(100000 / dynamicWorkingDays).toLocaleString()}</li>
                  <li>• Per-minute rate: ₦{(100000 / dynamicWorkingDays / (10 * 60)).toFixed(2)}/min</li>
                  <li>• 30 mins late = ₦{Math.round(30 * (100000 / dynamicWorkingDays / (10 * 60))).toLocaleString()} deduction</li>
                  <li>• Half-day absence = ₦{Math.round((100000 / dynamicWorkingDays) / 2).toLocaleString()} deduction</li>
                </ul>
              </div>

              {settingsSaveSuccess && (
                <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm font-medium">
                  <CheckCircle size={16} />
                  Settings saved successfully!
                </div>
              )}

              <div className="flex justify-end gap-4 mt-4">
                <button
                  onClick={() => setShowSettingsEdit(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={settingsSaveSuccess}
                >
                  Cancel
                </button>
                <button
                  onClick={saveDeductionSettings}
                  disabled={actionLoading || settingsSaveSuccess}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors flex items-center gap-2"
                >
                  {actionLoading ? (
                    <>
                      <Loader className="animate-spin" size={18} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Save Settings
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg border border-orange-200">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="text-orange-600" size={18} />
                  <h4 className="font-medium text-orange-800">Lateness Grace</h4>
                </div>
                <p className="text-2xl font-bold text-orange-600">{deductionSettings.latenessGraceMinutes} min</p>
                <p className="text-xs text-orange-700 mt-1">No deduction within grace period</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-yellow-200">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="text-yellow-600" size={18} />
                  <h4 className="font-medium text-yellow-800">Early Leave Grace</h4>
                </div>
                <p className="text-2xl font-bold text-yellow-600">{deductionSettings.earlyLeaveGraceMinutes} min</p>
                <p className="text-xs text-yellow-700 mt-1">No deduction within grace period</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="text-purple-600" size={18} />
                  <h4 className="font-medium text-purple-800">Absence Type</h4>
                </div>
                <p className="text-lg font-bold text-purple-600">
                  {deductionSettings.absencePenalty === 'shift_based' 
                    ? 'Shift-Based' 
                    : 'Full Day'}
                </p>
                <p className="text-xs text-purple-700 mt-1">
                  {deductionSettings.absencePenalty === 'shift_based' 
                    ? 'Supports half-day (50%)' 
                    : 'Always full day rate'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FIXED: Staff Payroll Setup - Only Active Staff with IMPROVED text visibility */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Users className="text-blue-600" size={24} />
              Staff Payroll Configuration
            </h2>
            <p className="text-sm text-gray-600 mt-1">Active Staff Only</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              {activeStaff.length} active staff
            </span>
            {staff.length > activeStaff.length && (
              <span className="text-xs bg-orange-100 text-orange-700 px-3 py-1 rounded-full font-medium">
                {staff.length - activeStaff.length} terminated
              </span>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-3 p-4 py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-600">Loading staff data...</span>
          </div>
        )}

        {!loading && activeStaff.length === 0 && (
          <div className="text-center py-8">
            <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500 text-lg">No active staff found</p>
            <p className="text-gray-400">
              {staff.length > 0 
                ? `All ${staff.length} staff members are terminated or inactive` 
                : 'Add staff members to configure their payroll'
              }
            </p>
          </div>
        )}

        {!loading && activeStaff.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {activeStaff.map(staffMember => (
              <div
                key={staffMember._id}
                className="group bg-gradient-to-br from-white to-gray-50 rounded-xl p-5 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* FIXED: Staff Header with FULL TEXT VISIBILITY */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1 pr-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {staffMember.firstName.charAt(0)}{staffMember.lastName ? staffMember.lastName.charAt(0) : ''}
                    </div>
                    <div className="min-w-0 flex-1">
                      {/* FIXED: Remove truncate, use word-break instead */}
                      <h3 className="font-bold text-gray-900 text-base leading-tight break-words" title={`${staffMember.firstName} ${staffMember.lastName}`}>
                        {staffMember.firstName} {staffMember.lastName}
                      </h3>
                      <div className="flex flex-col gap-1 mt-1">
                        {/* FIXED: Full visibility for position */}
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium inline-block w-fit break-words max-w-full" title={staffMember.position || 'N/A'}>
                          {staffMember.position || 'N/A'}
                        </span>
                        {/* FIXED: Full visibility for department */}
                        <span className="text-xs text-gray-600 break-words" title={staffMember.department || 'N/A'}>
                          {staffMember.department || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleStaffSelect(staffMember)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transform hover:scale-110 flex-shrink-0"
                    title="Edit Payroll"
                  >
                    <Edit size={16} />
                  </button>
                </div>

                {/* Salary Information with proper spacing */}
                <div className="space-y-3">
                  {/* Base Salary */}
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-xs font-medium text-gray-600">Monthly Base</span>
                      <span className="font-bold text-green-600 text-sm">
                        {staffMember.baseSalary ? formatCurrency(staffMember.baseSalary) : (
                          <span className="text-red-500 text-xs">Not set</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {staffMember.baseSalary > 0 && (
                    <>
                      {/* Allowances */}
                      {((staffMember.housingAllowance || 0) + (staffMember.transportAllowance || 0) + (staffMember.mealAllowance || 0) + (staffMember.otherAllowances || 0)) > 0 && (
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                          <div className="flex justify-between items-center gap-2">
                            <span className="text-xs font-medium text-blue-700">Allowances</span>
                            <span className="font-semibold text-blue-600 text-sm">
                              {formatCurrency(
                                (staffMember.housingAllowance || 0) + 
                                (staffMember.transportAllowance || 0) + 
                                (staffMember.mealAllowance || 0) + 
                                (staffMember.otherAllowances || 0)
                              )}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Gross Pay */}
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200">
                        <div className="flex justify-between items-center gap-2">
                          <span className="text-xs font-bold text-green-800">Gross Pay</span>
                          <span className="font-bold text-green-700 text-sm">
                            {formatCurrency(
                              staffMember.baseSalary + 
                              (staffMember.housingAllowance || 0) + 
                              (staffMember.transportAllowance || 0) + 
                              (staffMember.mealAllowance || 0) +
                              (staffMember.otherAllowances || 0)
                              )}
                            </span>
                            </div>
                          </div>
                        </>
                        )}
                      </div>
                    
                    {/* Quick Actions */}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <button
                onClick={() => handleStaffSelect(staffMember)}
                className="w-full text-center text-blue-600 hover:text-blue-800 text-sm font-medium py-2 hover:bg-blue-50 rounded-lg transition-colors"
              >
                Configure Payroll
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>

  {/* Staff Payroll Edit Modal */}
  {showEditModal && selectedStaff && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl max-w-4xl w-full my-8 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6 sticky top-0 bg-white pb-4 border-b z-10">
          <h2 className="text-2xl font-semibold text-gray-900">
            Edit Payroll for {selectedStaff.firstName} {selectedStaff.lastName}
          </h2>
          <button
            onClick={() => {
              setShowEditModal(false);
              setSelectedStaff(null);
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Base Salary (Monthly)</label>
              <input
                type="number"
                value={staffPayrollConfig.baseSalary}
                onChange={(e) => setStaffPayrollConfig({
                  ...staffPayrollConfig,
                  baseSalary: parseInt(e.target.value) || ''
                })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Housing Allowance</label>
              <input
                type="number"
                value={staffPayrollConfig.housingAllowance}
                onChange={(e) => setStaffPayrollConfig({
                  ...staffPayrollConfig,
                  housingAllowance: parseInt(e.target.value) || 0
                })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Transport Allowance</label>
              <input
                type="number"
                value={staffPayrollConfig.transportAllowance}
                onChange={(e) => setStaffPayrollConfig({
                  ...staffPayrollConfig,
                  transportAllowance: parseInt(e.target.value) || 0
                })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Meal Allowance</label>
              <input
                type="number"
                value={staffPayrollConfig.mealAllowance}
                onChange={(e) => setStaffPayrollConfig({
                  ...staffPayrollConfig,
                  mealAllowance: parseInt(e.target.value) || 0
                })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Other Allowances</label>
              <input
                type="number"
                value={staffPayrollConfig.otherAllowances}
                onChange={(e) => setStaffPayrollConfig({
                  ...staffPayrollConfig,
                  otherAllowances: parseInt(e.target.value) || 0
                })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tax Percentage</label>
              <input
                type="number"
                value={staffPayrollConfig.taxPercentage}
                onChange={(e) => setStaffPayrollConfig({
                  ...staffPayrollConfig,
                  taxPercentage: parseFloat(e.target.value) || 0
                })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pension Percentage</label>
              <input
                type="number"
                value={staffPayrollConfig.pensionPercentage}
                onChange={(e) => setStaffPayrollConfig({
                  ...staffPayrollConfig,
                  pensionPercentage: parseFloat(e.target.value) || 0
                })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
          </div>

          {/* UPDATED: Calculated Rates Display with refreshKey */}
          {staffPayrollConfig.baseSalary > 0 && (
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-lg border-2 border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-4 text-lg flex items-center gap-2">
                <Calculator className="text-blue-600" size={20} />
                Calculated Rates (Auto-computed & Real-time)
              </h4>
              
              <CalculatedRatesDisplay 
                baseSalary={staffPayrollConfig.baseSalary}
                staffId={selectedStaff._id}
                refreshKey={refreshKey}
              />
              
              <div className="mt-4 p-3 bg-white rounded border border-blue-200">
                <p className="text-xs text-gray-600 leading-relaxed">
                  <strong className="text-blue-700">Formula:</strong> Per-minute rate = (Base Salary ÷ Working Days) ÷ (Full Shift Hours × 60)
                  <br />
                  <strong className="text-blue-700 mt-1 inline-block">Note:</strong> Rates are calculated from the staff's full-day reference shift set in Shift Management.
                </p>
              </div>
            </div>
          )}

          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Bank Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                <input
                  type="text"
                  value={staffPayrollConfig.bankDetails.bankName}
                  onChange={(e) => setStaffPayrollConfig({
                    ...staffPayrollConfig,
                    bankDetails: { ...staffPayrollConfig.bankDetails, bankName: e.target.value }
                  })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                <input
                  type="text"
                  value={staffPayrollConfig.bankDetails.accountNumber}
                  onChange={(e) => setStaffPayrollConfig({
                    ...staffPayrollConfig,
                    bankDetails: { ...staffPayrollConfig.bankDetails, accountNumber: e.target.value }
                  })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Name</label>
                <input
                  type="text"
                  value={staffPayrollConfig.bankDetails.accountName}
                  onChange={(e) => setStaffPayrollConfig({
                    ...staffPayrollConfig,
                    bankDetails: { ...staffPayrollConfig.bankDetails, accountName: e.target.value }
                  })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

         {/* Monthly Salary Summary */}
          <div className="mt-6 bg-gray-50 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Monthly Salary Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Base Salary (Monthly)</p>
                <p className="font-semibold text-gray-900">{formatCurrency(staffPayrollConfig.baseSalary || 0)}</p>
              </div>
              <div>
                <p className="text-gray-600">Total Allowances (Monthly)</p>
                <p className="font-semibold text-green-600">
                  {formatCurrency(
                    (staffPayrollConfig.housingAllowance || 0) +
                    (staffPayrollConfig.transportAllowance || 0) +
                    (staffPayrollConfig.mealAllowance || 0) +
                    (staffPayrollConfig.otherAllowances || 0)
                  )}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Monthly Gross Pay</p>
                <p className="font-semibold text-blue-600">
                  {formatCurrency(
                    (parseInt(staffPayrollConfig.baseSalary) || 0) +
                    (staffPayrollConfig.housingAllowance || 0) +
                    (staffPayrollConfig.transportAllowance || 0) +
                    (staffPayrollConfig.mealAllowance || 0) +
                    (staffPayrollConfig.otherAllowances || 0)
                  )}
                </p>
              </div>
            </div>
          </div>

          {staffSaveSuccess && (
            <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm font-medium mt-4">
              <CheckCircle size={16} />
              Payroll configuration saved successfully!
            </div>
          )}

          <div className="flex justify-end gap-4 mt-6 sticky bottom-0 bg-white pt-4 border-t z-10">
            <button
              onClick={() => {
                setShowEditModal(false);
                setSelectedStaff(null);
              }}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={staffSaveSuccess}
            >
              Cancel
            </button>
            <button
              onClick={saveStaffPayrollConfig}
              disabled={actionLoading || staffSaveSuccess}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {actionLoading ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Configuration
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )}

     {/* NEW SECTION: PAYROLL HISTORICAL BACKFILL TOOL - BUTTON ONLY */}
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl shadow-xl border border-red-200 overflow-hidden">
        <div className="bg-gradient-to-r from-red-600 to-orange-600 px-6 py-4">
          <h3 className="text-xl font-semibold text-white flex items-center gap-2">
            <Calculator className="text-2xl" />
            Advanced Payroll Tools
          </h3>
          <p className="text-red-50 text-sm mt-1">Expert-level payroll management features</p>
        </div>
        
        <div className="p-6">
          <div className="bg-white rounded-lg border-2 border-red-300 p-6">
            <h4 className="font-semibold text-gray-900 mb-2 text-lg">Historical Payroll Rebuild</h4>
            <p className="text-gray-600 text-sm mb-4">
              Completely rebuild payroll data for a specific date range using current settings and shift configurations.
            </p>
            
            <button
              onClick={() => setShowBackfillModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg hover:opacity-90 transition-all font-semibold shadow-lg flex items-center justify-center gap-2"
            >
              <AlertCircle size={20} />
              Open Payroll Rebuild Tool
            </button>
          </div>
        </div>
      </div>

      {/* PAYROLL BACKFILL MODAL */}
      {showBackfillModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-red-600 to-orange-600 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <AlertCircle className="text-2xl" />
                Payroll Historical Rebuild Tool
              </h3>
              <button
                onClick={() => setShowBackfillModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={24} />
                  <div>
                    <p className="text-sm font-bold text-red-800 mb-2">
                      ⚠️ WARNING: This is a destructive operation!
                    </p>
                    <p className="text-sm text-red-800 mb-3">
                      This tool will:
                    </p>
                    <ul className="text-sm text-red-700 space-y-1 ml-4">
                      <li>❌ <strong>DELETE</strong> all existing payroll records for the selected date range</li>
                      <li>🔄 Rebuild payroll data from scratch using current settings</li>
                      <li>✅ Use shift-based dynamic rate calculations</li>
                      <li>✅ Apply current grace periods and deduction rules</li>
                      <li>✅ Respect temporary shifts (priority over regular)</li>
                      <li>✅ Honor approved leaves (paid leave)</li>
                    </ul>
                    <p className="text-sm text-red-800 mt-3 font-semibold">
                      💡 Use this when: Payroll settings changed, shifts were updated retroactively, or data needs complete recalculation.
                    </p>
                  </div>
                </div>
              </div>

              <PayrollBackfillForm loading={actionLoading} onClose={() => setShowBackfillModal(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Payroll Backfill Form Component (UPDATED with onClose prop)
const PayrollBackfillForm = ({ loading, onClose }) => {
  const getAuthToken = () => {
    return localStorage.getItem('adminAuthToken') || localStorage.getItem('kioskAuthToken') || localStorage.getItem('authToken');
  };

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      alert('End date cannot be before start date');
      return;
    }

    // Double confirmation for destructive operation
    const confirmed = window.confirm(
      `⚠️ DESTRUCTIVE OPERATION WARNING ⚠️\n\n` +
      `This will DELETE all payroll records from ${startDate} to ${endDate} and rebuild them from scratch.\n\n` +
      `This action CANNOT be undone.\n\n` +
      `Are you absolutely sure you want to continue?`
    );

    if (!confirmed) return;

    const doubleConfirm = window.confirm(
      `Final confirmation:\n\n` +
      `Delete and rebuild payroll for ${startDate} to ${endDate}?\n\n` +
      `Click OK to proceed or Cancel to abort.`
    );

    if (!doubleConfirm) return;

    try {
      setIsProcessing(true);
      setResult(null);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/payroll/recalculate-historical`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ 
          startFromDate: startDate, 
          endFromDate: endDate 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Backfill failed');
      }

      setResult({
        success: true,
        message: data.message,
        details: data
      });

      alert(
        `✅ ${data.message}\n\n` +
        `Processing in background. This may take several minutes.\n\n` +
        `Check server logs for detailed progress.`
      );

      // Close modal after 2 seconds on success
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err) {
      console.error('Payroll backfill error:', err);
      setResult({
        success: false,
        message: err.message
      });
      alert(`❌ Error: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Start Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            required
            disabled={loading || isProcessing}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            End Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            required
            disabled={loading || isProcessing}
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold"
          disabled={isProcessing}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || isProcessing || !startDate || !endDate}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold shadow-lg flex items-center justify-center gap-2"
        >
          {isProcessing ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Processing...
            </>
          ) : (
            <>
              <AlertCircle /> Rebuild Payroll Data
            </>
          )}
        </button>
      </div>

      {result && (
        <div className={`mt-4 p-4 rounded-lg border-l-4 ${result.success ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
          <p className={`text-sm font-semibold ${result.success ? 'text-green-800' : 'text-red-800'}`}>
            {result.success ? '✅ Backfill Started!' : '❌ Error'}
          </p>
          <p className={`text-sm mt-1 ${result.success ? 'text-green-700' : 'text-red-700'}`}>
            {result.message}
          </p>
          {result.success && result.details && (
            <div className="mt-2 text-xs text-green-600">
              <p>Date range: {result.details.startDate} to {result.details.endDate || 'present'}</p>
            </div>
          )}
        </div>
      )}
    </form>
  );
};

export default PayrollSetup;